import {
  commerceFetch,
  commerceFetchForOptionalSession,
  commerceFetchStrict,
} from "./commerce-api";
import { News } from "./news";
import { OrderCargo } from "./order-cargo";
import { Slide } from "./slides";
import { MenuItem } from "./menu";
import { Opportunity } from "./opportunities";
import { Setting } from "./settings";
import { Category } from "@/app/types/homepage";
import { PartnerLogo } from "./partner-logo";
import { getServerApiBase } from "../server-api-base";

export interface PublicCategory {
  id: number;
  category: string;
  image_url: string | null;
  url: string;
  percentage?: number | null;
  priority?: boolean | null;
  products?: PublicProduct[];
}

export interface PublicProduct {
  id: number;
  title: string;
  image_url: string | null;
  url: string;
  price?: number;
  kid?: number;
  user_discount?: number | null;
}

export interface OrderProduct {
  id: number;
  title: string;
  image_url: string | null;
  colors: number[];
  sizes: number[];
  kid: number;
  /** Üründe kapak fotoğrafı zorunluysa true (admin / ürün kaydı). */
  cover_photo_required?: boolean;
}

export interface PublicColor {
  id: number;
  title: string;
  price: string;
}

export interface PublicSize {
  id: number;
  title: string;
  category: string;
}

export interface PublicPackage {
  id: number;
  title: string;
  customer_title: string;
  price: string;
  pagecounts: [number];
  size: string;
}

export interface PublicDesign {
  id: number;
  title: string;
  customer_title: string;
  price: string;
  package: number;
}

export interface PublicRetouch {
  id: number;
  title: string;
  customer_title: string;
  price: string;
  package: number;
}

export interface PublicPaper {
  id: number;
  title: string;
  price: string;
  packages: number[];
}

export interface PublicBox {
  id: number;
  title: string;
  price: string;
  package: number;
}

export interface PublicExtra {
  id: number;
  title: string;
  price: string;
}

export interface PublicPromotion {
  id: number;
  title: string;
  customer_title: string;
  price: string;
  package: number;
}

export interface PublicPageCount {
  id: number;
  title: string;
  price: string;
}
export interface PublicPsdProduct {
  id: number;
  title: string;
  image_url: string | null;
  url: string;
  colors: number[];
  sizes: number[];
}

export interface PublicCategoryForNavbar {
  categories: PublicCategory[];
  canvasCategories: PublicCategory[];
  printCategories: PublicCategory[];
  giftCategories: PublicCategory[];
  psdCategories: PublicCategory[];
}

export interface CreateOrderInput {
  image?: string | null;
  product?: string;
  stage: string | null;
  bride_name?: string | null;
  groom_name?: string | null;
  baby_name?: string | null;
  new_address?: string;
  price?: number;
  package_price?: number;
  paper_price?: number;
  color_price?: number;
  page_price?: number;
  promotion_price?: number;
  extra_price?: number;
  description?: string;
  chest?: string;
  chest_price?: number;
  discounted_price?: number;
  photo_count?: number | null;
  size?: number;
  package?: number;
  paper?: number;
  color?: number;
  page?: number;
  promotion?: number | null;
  design?: number | null;
  retouch?: number | null;
  design_price?: number;
  retouch_price?: number;
  member?: number | null;
  extras?: { extra: number; count: number }[];
  take_date?: string;
  cargo_type: number;
  cargo_price: number;
}

export interface Order {
  id: number;
  member: number;
  member_name?: string;
  size: number;
  size_name?: string;
  package: number;
  package_name?: string;
  paper: number;
  paper_name?: string;
  color: number;
  color_name?: string;
  page: number;
  page_name?: string;
  promotion: number | null;
  promotion_name?: string;
  /** Retrieve cevabında `extra` başlık (string); liste endpoint'inde id (number) olabilir. */
  extras?: {
    extra_id?: number;
    extra: number | string;
    count: number;
    price?: number | string;
  }[];
  extra_names?: string[];
  status: string;
  price: string;
  package_price: string;
  paper_price: string;
  color_price: string;
  page_price: string;
  promotion_price?: string;
  extra_price: string;
  description?: string;
  photo_count: number | null;
  date: string;
  delivery_date: string;
  take_date: string;
  new_address: string;
  new_address2?: string;
  description2?: string;
  cargo_status?: string;
  recipient_status?: string;
  product: string;
  stage: string;
  chest?: string;
  chest_price?: string;
  design?: string;
  design_name?: string;
  design_price?: string;
  retouch?: string;
  retouch_name?: string;
  retouch_price?: string;
  discounted_price?: string;
  bride_name?: string;
  groom_name?: string;
  baby_name?: string;
  images?: OrderImage[];
  /** GET /orders/{id}/ retrieve ile gelir; yüklenen fotoğraf adedi */
  image_upload_count?: number;
  customer_status?: string;
  studio_downloaded_at?: string | null;
  cover_photo_required?: boolean;
  /** Sipariş sahibi üyede admin tarafından işaretlenen PayTR zorunluluğu */
  paytr_payment_required?: boolean;
  /** Cüzdandan tahsilat yapıldıysa true */
  wallet_payment_applied?: boolean;
  /** Taslak sipariş müşteri tarafından silinebilir mi */
  customer_can_delete?: boolean;
}

export interface WalletTransactionRow {
  id: number;
  amount: string;
  balance_after: string;
  transaction_type: string;
  note: string;
  created_at: string;
  /** Sipariş ödemesinde albüm sipariş numarası */
  order_id?: number | null;
  canvas_order_id?: number | null;
  print_order_id?: number | null;
  gift_order_id?: number | null;
}

export interface OrderImage {
  id: number;
  image_url: string | null;
  image: string | null;
  order: number;
}

type ApiResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export interface CreateSimpleOrderInput {
  image: null;
  member: number | null;
  price: number;
  canvas: string;
  quantity: number;
  discounted_price: number;
  description?: string;
  cargo_type: number;
  cargo_price: number;
}

export interface Homepage {
  sliders: Slide[];
  news: News[];
  opportunities: Opportunity[];
  categories: Category[];
  middle_menu: MenuItem[];
  partners: PartnerLogo[];
  settings: any[];
}

export const createCanvasOrder = async (data: CreateSimpleOrderInput) => {
  return commerceFetch(`/orders/canvasorders/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const createPrintOrder = async (data: CreateSimpleOrderInput) => {
  return commerceFetch(`/orders/printorders/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const createGiftOrder = async (data: CreateSimpleOrderInput) => {
  return commerceFetch(`/orders/giftorders/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const getPublicCategories = async (
  page: number = 1,
  pageSize: number = 1000
): Promise<ApiResponse<PublicCategory>> => {
  const response = await fetch(
    `${getServerApiBase()}/products/publiccategories/?page=${page}&page_size=${pageSize}`
  );
  if (!response.ok) throw new Error("Failed to fetch categories");
  return response.json();
};

export const getCategoriesForNavbar =
  async (): Promise<PublicCategoryForNavbar> => {
    const response = await fetch(`${getServerApiBase()}/products/public/all-categories/`);
    if (!response.ok) throw new Error("Failed to fetch categories");
    return response.json();
  };

export const getPublicProducts = async (
  categoryId: number,
  page: number = 1,
  pageSize: number = 1000
): Promise<ApiResponse<PublicProduct>> => {
  const response = await fetch(
    `${getServerApiBase()}/products/publicproducts/?kid=${categoryId}&page=${page}&page_size=${pageSize}`
  );
  if (!response.ok) throw new Error("Failed to fetch products");
  return response.json();
};

export const getOrderProduct = async (id: number): Promise<OrderProduct> => {
  const response = await fetch(`${getServerApiBase()}/products/orderproducts/${id}/`);
  if (!response.ok) throw new Error("Failed to fetch product");
  return response.json();
};

export const getPublicColors = async (): Promise<ApiResponse<PublicColor>> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publiccolors/`);
  if (!response.ok) throw new Error("Failed to fetch colors");
  return response.json();
};

export const getPublicSizes = async (): Promise<ApiResponse<PublicSize>> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publicsizes/`);
  if (!response.ok) throw new Error("Failed to fetch sizes");
  return response.json();
};

export const getPublicPackages = async (): Promise<
  ApiResponse<PublicPackage>
> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publicpackages/`);
  if (!response.ok) throw new Error("Failed to fetch packages");
  return response.json();
};

export const getPublicPapers = async (): Promise<ApiResponse<PublicPaper>> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publicpapers/`);
  if (!response.ok) throw new Error("Failed to fetch papers");
  return response.json();
};

export const getPublicBoxes = async (): Promise<ApiResponse<PublicBox>> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publicboxes/`);
  if (!response.ok) throw new Error("Failed to fetch boxes");
  return response.json();
};

export const getPublicExtras = async (): Promise<ApiResponse<PublicExtra>> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publicekstras/`);
  if (!response.ok) throw new Error("Failed to fetch extras");
  return response.json();
};

export const getPublicPromotions = async (): Promise<
  ApiResponse<PublicPromotion>
> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publicpromotions/`);
  if (!response.ok) throw new Error("Failed to fetch promotions");
  return response.json();
};

export const getPublicPageCounts = async (): Promise<
  ApiResponse<PublicPageCount>
> => {
  const response = await fetch(`${getServerApiBase()}/orderprocess/publicpagecounts/`);
  if (!response.ok) throw new Error("Failed to fetch page counts");
  return response.json();
};

export const createOrder = async (data: CreateOrderInput) => {
  return await commerceFetch(`/orders/orders/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const deleteMyAlbumOrder = async (id: string) => {
  return commerceFetch(`/orders/orders/${id}/`, { method: "DELETE" });
};

export const createPsdOrder = async (data: CreateOrderInput) => {
  return await commerceFetch(`/orders/orderpsd/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const getOrders = async (
  page: number = 1
): Promise<ApiResponse<Order>> => {
  return await commerceFetch(`/orders/orders/?page=${page}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getOrder = async (id: string): Promise<Order> => {
  return await commerceFetch(`/orders/orders/${id}/`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const updateOrderStatus = async (id: string, data: any) => {
  return await commerceFetchStrict(`/orders/orders/${id}/update_customer_status/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updateCanvasOrderCustomerStatus = async (id: string, data: any) => {
  return await commerceFetchStrict(`/orders/canvasorders/${id}/update_customer_status/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updatePrintOrderCustomerStatus = async (id: string, data: any) => {
  return await commerceFetchStrict(`/orders/printorders/${id}/update_customer_status/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updateGiftOrderCustomerStatus = async (id: string, data: any) => {
  return await commerceFetchStrict(`/orders/giftorders/${id}/update_customer_status/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const getPublicPsdCategories = async (): Promise<
  ApiResponse<PublicCategory>
> => {
  const response = await fetch(`${getServerApiBase()}/products/publicpsdcategories/`);
  if (!response.ok) throw new Error("Failed to fetch canvas categories");
  return response.json();
};

export const getPublicPsdProducts = async (
  categoryId: number
): Promise<ApiResponse<PublicProduct>> => {
  const response = await fetch(
    `${getServerApiBase()}/products/publicpsds/?kid=${categoryId}`
  );
  if (!response.ok) throw new Error("Failed to fetch print products");
  return response.json();
};

export const getOrderPsdProduct = async (id: number): Promise<OrderProduct> => {
  const response = await fetch(`${getServerApiBase()}/products/orderpsds/${id}/`);
  if (!response.ok) throw new Error("Failed to fetch print product");
  return response.json();
};

export async function getPsdOrders(
  page: number = 1
): Promise<ApiResponse<Order>> {
  return await commerceFetch(`/orders/orderpsd/?page=${page}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
}

export const getPsdOrder = async (id: number): Promise<Order> => {
  return await commerceFetch(`/orders/orderpsd/${id}/`, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getPublicCanvasCategories = async (): Promise<
  ApiResponse<PublicCategory>
> => {
  const response = await fetch(`${getServerApiBase()}/products/publiccanvascategories/`);
  if (!response.ok) throw new Error("Failed to fetch canvas categories");
  return response.json();
};

export const getPublicGiftCategories = async (): Promise<
  ApiResponse<PublicCategory>
> => {
  const response = await fetch(`${getServerApiBase()}/products/publicgiftcategories/`);
  if (!response.ok) throw new Error("Failed to fetch gift categories");
  return response.json();
};

export const getPublicPrintCategories = async (): Promise<
  ApiResponse<PublicCategory>
> => {
  const response = await fetch(`${getServerApiBase()}/products/publicprintcategories/`);
  if (!response.ok) throw new Error("Failed to fetch print categories");
  return response.json();
};

export const getPublicCanvasProductsByUrl = async (
  categoryId: number
): Promise<ApiResponse<PublicProduct>> => {
  const response = await fetch(
    `${getServerApiBase()}/products/publiccanvas/?kid=${categoryId}`
  );
  if (!response.ok) throw new Error("Failed to fetch canvas products");
  return response.json();
};

export const getPublicPrintProductsByUrl = async (
  categoryId: number
): Promise<ApiResponse<PublicProduct>> => {
  const response = await fetch(
    `${getServerApiBase()}/products/publicprints/?kid=${categoryId}`
  );
  if (!response.ok) throw new Error("Failed to fetch print products");
  return response.json();
};

export const getPublicGiftProductsByUrl = async (
  categoryId: number
): Promise<ApiResponse<PublicProduct>> => {
  const response = await fetch(
    `${getServerApiBase()}/products/publicgifts/?kid=${categoryId}`
  );
  if (!response.ok) throw new Error("Failed to fetch gift products");
  return response.json();
};

export interface SimpleOrder {
  id: number;
  member: number;
  member_name?: string;
  status: string;
  customer_status?: string;
  studio_downloaded_at?: string | null;
  price: string;
  canvas: string;
  canvas_name?: string;
  quantity: number;
  discounted_price: string;
  description?: string;
  date: string;
  images?: OrderImage[];
  paytr_payment_required?: boolean;
  wallet_payment_applied?: boolean;
}

export interface SimpleOrdersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SimpleOrder[];
}

export const getCanvasOrders = async (
  page: number = 1
): Promise<SimpleOrdersResponse> => {
  return commerceFetch(`/orders/canvasorders/?page=${page}`);
};

export const getCanvasOrder = async (id: string): Promise<SimpleOrder> => {
  return commerceFetch(`/orders/canvasorders/${id}/`);
};

export const deleteCanvasOrder = async (id: string): Promise<void> => {
  return commerceFetch(`/orders/canvasorders/${id}/`, {
    method: 'DELETE',
  });
};

export const getPrintOrders = async (
  page: number = 1
): Promise<SimpleOrdersResponse> => {
  return commerceFetch(`/orders/printorders/?page=${page}`);
};

export const getPrintOrder = async (id: string): Promise<SimpleOrder> => {
  return commerceFetch(`/orders/printorders/${id}/`);
};

export const getGiftOrders = async (
  page: number = 1
): Promise<SimpleOrdersResponse> => {
  return commerceFetch(`/orders/giftorders/?page=${page}`);
};

export const getGiftOrder = async (id: string): Promise<SimpleOrder> => {
  return commerceFetch(`/orders/giftorders/${id}/`);
};

export const getPublicPrintProduct = async (
  id: number
): Promise<PublicProduct> => {
  const response = await fetch(`${getServerApiBase()}/products/publicprints/${id}/`);
  if (!response.ok) throw new Error("Failed to fetch print product");
  return response.json();
};

export const getPublicCanvasProduct = async (
  id: number
): Promise<PublicProduct> => {
  const response = await fetch(`${getServerApiBase()}/products/publiccanvas/${id}/`);
  if (!response.ok) throw new Error("Failed to fetch canvas product");
  return response.json();
};

export const getPublicGiftProduct = async (
  id: number
): Promise<PublicProduct> => {
  const response = await fetch(`${getServerApiBase()}/products/publicgifts/${id}/`);
  if (!response.ok) throw new Error("Failed to fetch gift product");
  return response.json();
};

export const getUserDiscount = async (): Promise<{
  discount: number | null;
}> => {
  try {
    const response = await commerceFetch(`/user/user_discount/`);
    return { discount: response.discount };
  } catch (error) {
    return { discount: null };
  }
};

export const getOrderCargos = async (): Promise<OrderCargo[]> => {
  const response = await fetch(getServerApiBase() + "/settings/ordercargo/");
  if (!response.ok) throw new Error("Failed to fetch order cargos");
  return response.json();
};

export const getPublicSlides = async (): Promise<Slide[]> => {
  const response = await fetch(`${getServerApiBase()}/settings/slides-public/`);
  const data = await response.json();
  return data;
};

export const getPublicNews = async (): Promise<News[]> => {
  const response = await fetch(`${getServerApiBase()}/settings/news-public/`);
  const data = await response.json();
  return data;
};

export const getPublicMenuItems = async (): Promise<MenuItem[]> => {
  const response = await fetch(`${getServerApiBase()}/settings/menu-public/`);
  const data = await response.json();
  return data;
};

export const getPublicOpportunities = async (): Promise<Opportunity[]> => {
  const response = await fetch(`${getServerApiBase()}/settings/opportunities-public/`);
  const data = await response.json();
  return data;
};

export const getPublicSetting = async (id: number): Promise<Setting> => {
  const response = await fetch(`${getServerApiBase()}/settings/setting-public/?id=${id}`);
  if (!response.ok) throw new Error(`Failed to fetch setting with ID ${id}`);
  return response.json();
};

export const getHomepage = async (): Promise<Homepage> => {
  const response = await fetch(`${getServerApiBase()}/homepage/`);
  if (!response.ok) throw new Error("Failed to fetch homepage");
  return response.json();
};

// Unified order data interface
export interface OrderDataResponse {
  product: OrderProduct;
  colors: PublicColor[];
  sizes: PublicSize[];
  packages: PublicPackage[];
  designs: PublicDesign[];
  retouches: PublicRetouch[];
  papers: PublicPaper[];
  boxes: PublicBox[];
  extras: PublicExtra[];
  promotions: PublicPromotion[];
  page_counts: PublicPageCount[];
  categories: PublicCategory[];
  user_discount: number | null;
}

export const getOrderData = async (
  productId: string
): Promise<OrderDataResponse> => {
  const response = await commerceFetchForOptionalSession(
    `/order-data/?productId=${productId}`
  );
  return response;
};

// Test function to diagnose API issues
export const testOrderDataAPI = async (productId: string): Promise<any> => {
  const response = await fetch(
    `${getServerApiBase()}/order-data/?productId=${productId}`
  );
  const data = await response.json();
  return data;
};

export const updateOrderBarcode = async (barcode: string): Promise<any> => {
  const url =
    `${getServerApiBase()}/order-barcode/?barcode=${encodeURIComponent(barcode)}` +
    `&_ts=${Date.now()}`;
  const response = await fetch(url, {
    cache: "no-store",
    next: { revalidate: 0 },
  });
  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    const detail =
      typeof payload === "string"
        ? payload
        : payload?.detail || payload?.error || JSON.stringify(payload);
    throw new Error(`Barkod onayı başarısız: ${response.status} ${detail}`);
  }
  return payload;
};

export const getPaytrPublicMeta = async (): Promise<{ paytr_active: boolean }> => {
  const res = await fetch(`${getServerApiBase()}/paytr-public/`);
  if (!res.ok) return { paytr_active: false };
  return res.json();
};

/** Albüm siparişi PayTR token (giriş yapmış müşteri veya yetkili). */
export const initiateAlbumOrderPayment = async (
  orderId: number
): Promise<{ token: string; merchant_oid?: string }> => {
  return commerceFetchStrict(`/initiate-payment/${orderId}/`, {
    method: "POST",
  }) as Promise<{ token: string; merchant_oid?: string }>;
};

export type SimplePayKind = "canvas" | "print" | "gift";

export const initiateSimpleOrderPayment = async (
  kind: SimplePayKind,
  orderId: number
): Promise<{ token: string; merchant_oid?: string }> => {
  return commerceFetchStrict(`/initiate-payment/${kind}/${orderId}/`, {
    method: "POST",
  }) as Promise<{ token: string; merchant_oid?: string }>;
};

export type PaytrSyncResult = {
  synced: boolean;
  reason?: string;
  detail?: string;
  hint?: string;
};

/** PayTR bildirimi gecikirse ödeme sonrası dönüşte durumu PayTR API ile senkronize eder. */
export const syncAlbumPaytrStatus = async (
  orderId: number
): Promise<PaytrSyncResult> => {
  return commerceFetchStrict(`/sync-paytr/${orderId}/`, {
    method: "POST",
  }) as Promise<PaytrSyncResult>;
};

export const syncSimpleOrderPaytrStatus = async (
  kind: SimplePayKind,
  orderId: number
): Promise<PaytrSyncResult> => {
  return commerceFetchStrict(`/sync-paytr/${kind}/${orderId}/`, {
    method: "POST",
  }) as Promise<PaytrSyncResult>;
};

export const getMyWalletTransactions = async (): Promise<
  WalletTransactionRow[]
> => {
  return commerceFetchStrict("/wallet/transactions/", {
    method: "GET",
  }) as Promise<WalletTransactionRow[]>;
};

/** Oturum açmış müşteri kendi cüzdan yükleme PayTR oturumunu başlatır (tek seferlik token). */
export async function createMyWalletTopupSession(): Promise<{ token: string }> {
  return commerceFetchStrict("/wallet/topup/session/", {
    method: "POST",
  }) as Promise<{ token: string }>;
}

/** Müşteri oturumu ile profil (istemci bileşenlerinde server action). */
export type CommerceMeUser = {
  id: number;
  username: string;
  email: string;
  full_name: string;
  phone: string;
  company: string;
  address: string;
  city: string;
  district: string;
  wallet_balance?: string;
  paytr_payment_required?: boolean;
};

export async function getCommerceMe(): Promise<
  CommerceMeUser | { error: string }
> {
  return commerceFetch(`/user/me/`, { method: "GET" });
}

/** Tarayıcıda cüzdan yükleme sayfası için (token ile, giriş gerekmez). */
export async function getWalletTopupMetaPublic(
  token: string
): Promise<{
  valid: boolean;
  paytr_active: boolean;
  already_used?: boolean;
}> {
  const base = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
  if (!base) {
    throw new Error("NEXT_PUBLIC_API_URL tanımlı değil");
  }
  const res = await fetch(
    `${base}/wallet/topup/meta/?token=${encodeURIComponent(token)}`
  );
  return res.json();
}

export async function initiateWalletTopupPublic(
  token: string,
  amount: string
): Promise<{ token: string; merchant_oid?: string }> {
  const base = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
  if (!base) {
    throw new Error("NEXT_PUBLIC_API_URL tanımlı değil");
  }
  const res = await fetch(`${base}/wallet/topup/initiate/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, amount }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      typeof data.error === "string" ? data.error : "Ödeme başlatılamadı"
    );
  }
  return data;
}

/** PayTR bildirimi gecikirse: dönüş sonrası bakiyeyi durum sorgusu ile günceller. */
export async function syncWalletTopupPublic(token: string): Promise<{
  synced?: boolean;
  reason?: string;
  detail?: string;
  hint?: string;
  error?: string;
}> {
  const base = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
  if (!base) {
    throw new Error("NEXT_PUBLIC_API_URL tanımlı değil");
  }
  const res = await fetch(`${base}/wallet/topup/sync/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  return res.json().catch(() => ({}));
}
